import React from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import theme from './styles/theme';
import Layout from './components/Layout/Layout';
import AdminLayout from './components/AdminLayout/AdminLayout';
import AdminAuthGuard from './components/AdminAuthGuard/AdminAuthGuard';

// Public pages
import HomePage from './pages/HomePage/HomePage';
import SearchPage from './pages/SearchPage/SearchPage';
import DocumentPage from './pages/DocumentPage/DocumentPage';
import ValidationPage from './pages/ValidationPage/ValidationPage';
import AcompanhamentoPage from './pages/AcompanhamentoPage/AcompanhamentoPage';

// Admin pages
import AdminLoginPage from './pages/AdminLoginPage/AdminLoginPage';
import AdminHomePage from './pages/AdminHomePage/AdminHomePage';
import AdminServidoresPage from './pages/AdminServidoresPage/AdminServidoresPage';
import AdminDocumentosPage from './pages/AdminDocumentosPage/AdminDocumentosPage';

// Advogado pages
import AdvogadoHomePage        from './pages/AdvogadoHomePage/AdvogadoHomePage';
import AdvogadoServidoresPage  from './pages/AdvogadoServidoresPage/AdvogadoServidoresPage';
import AdvogadoDocumentosPage  from './pages/AdvogadoDocumentosPage/AdvogadoDocumentosPage';

// ── Helper ────────────────────────────────────────────────────────────────────
const getAdminRole = (): string => {
  try {
    return JSON.parse(localStorage.getItem('admin_user') || '{}').role || '';
  } catch {
    return '';
  }
};

// ── Guards / wrappers ─────────────────────────────────────────────────────────

/** ADMIN e SUPER_ADMIN — com AdminLayout */
const AdminPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AdminAuthGuard>
    <AdminLayout>{children}</AdminLayout>
  </AdminAuthGuard>
);

/** ADVOGADO — sem AdminLayout, redireciona outros roles para /admin */
const AdvogadoGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AdminAuthGuard>
    {getAdminRole() === 'ADVOGADO'
      ? <>{children}</>
      : <Navigate to="/admin" replace />}
  </AdminAuthGuard>
);

/** /admin — redireciona ADVOGADO para a home dele, outros ficam no AdminLayout */
const AdminRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AdminAuthGuard>
    {getAdminRole() === 'ADVOGADO'
      ? <Navigate to="/admin/advogado" replace />
      : <AdminLayout>{children}</AdminLayout>}
  </AdminAuthGuard>
);

// ─────────────────────────────────────────────────────────────────────────────

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>

          {/* ── Públicas ──────────────────────────────────────────────── */}
          <Route path="/"                    element={<Layout><HomePage /></Layout>} />
          <Route path="/consulta"            element={<Layout><SearchPage /></Layout>} />
          <Route path="/documentos"          element={<Layout><DocumentPage /></Layout>} />
          <Route path="/documentos/:servidorId" element={<Layout><DocumentPage /></Layout>} />
          <Route path="/validar-email"       element={<Layout><ValidationPage /></Layout>} />
          <Route path="/auth/:token"         element={<Layout><ValidationPage /></Layout>} />
          <Route path="/acompanhar"          element={<Layout><AcompanhamentoPage /></Layout>} />

          {/* ── Login ─────────────────────────────────────────────────── */}
          <Route path="/admin/login"         element={<Layout><AdminLoginPage /></Layout>} />

          {/* ── /admin → redireciona ADVOGADO, serve ADMIN normalmente ── */}
          <Route path="/admin"               element={<AdminRedirect><AdminHomePage /></AdminRedirect>} />

          {/* ── ADMIN / SUPER_ADMIN ────────────────────────────────────── */}
          <Route path="/admin/servidores"    element={<AdminPage><AdminServidoresPage /></AdminPage>} />
          <Route path="/admin/documentos"    element={<AdminPage><AdminDocumentosPage /></AdminPage>} />

          {/* ── ADVOGADO ──────────────────────────────────────────────── */}
          <Route path="/admin/advogado"            element={<AdvogadoGuard><AdvogadoHomePage /></AdvogadoGuard>} />
          <Route path="/admin/advogado/servidores" element={<AdvogadoGuard><AdvogadoServidoresPage /></AdvogadoGuard>} />
          <Route path="/admin/advogado/documentos" element={<AdvogadoGuard><AdvogadoDocumentosPage /></AdvogadoGuard>} />

          {/* ── Fallback ───────────────────────────────────────────────── */}
          <Route path="*"                    element={<Navigate to="/" />} />

        </Routes>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { background: '#363636', color: '#fff' },
          }}
        />
      </Router>
    </ThemeProvider>
  );
}

export default App;